import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, RouterLink, UrlTree } from '@angular/router';
import { ResourceHintsService } from '@core/services/resource-hints.service';
import Splide, { Options } from '@splidejs/splide';

import { HomePageCarouselItemDto } from 'src/app/articles-manager/interfaces/auction.interface';
import { LanguageService } from 'src/app/shared/services/language.service';
import { environment } from 'src/environments/environment';

interface SlideItemView {
  idItem: number;
  imageUrl: string | null;
  mobileImageUrl: string | null;
  youtubeUrl: SafeResourceUrl | null;
  youtubeUrlMobile: SafeResourceUrl | null;
  linkUrl: string | null;
  itemOrder: number;
}

@Component({
  selector: 'app-slideshow',
  templateUrl: './slideshow.component.html',
  styleUrls: ['./slideshow.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SlideshowComponent implements AfterViewInit, OnDestroy {
  @ViewChild('splide') splideRef?: ElementRef<HTMLElement>;
  private splideInstance?: Splide;
  private rebuildTimeoutId?: ReturnType<typeof setTimeout>;
  private videoControlTimeoutId?: ReturnType<typeof setTimeout>;
  private readonly isViewInitialized = signal(false);
  private trackpadCooldownTimeoutId?: ReturnType<typeof setTimeout>;
  private readonly videoCommandTimeoutIds = new Set<
    ReturnType<typeof setTimeout>
  >();
  private isDestroyed = false;
  private lastSlidesSignature = '';
  private readonly youtubeRevealFallbackDelay = 3000;
  private readonly youtubeReadyStateValues = new Set([1, 3]);
  private readonly splideVideoSyncEvents: Array<
    'moved' | 'active' | 'visible'
  > = ['moved', 'active', 'visible'];
  private trackpadWheelListener?: (event: WheelEvent) => void;
  private trackpadScrollCooldown = false;
  private lastMobileView?: boolean;
  private initializedYouTubeIframes = new WeakSet<HTMLIFrameElement>();
  private readonly youtubeRevealTimeoutIds = new Map<
    number,
    ReturnType<typeof setTimeout>
  >();

  private readonly pageShowListener = () => {
    this.scheduleActiveVideoResume();
  };
  private readonly visibilityChangeListener = () => {
    if (
      isPlatformBrowser(this.platformId) &&
      document.visibilityState === 'visible'
    ) {
      this.scheduleActiveVideoResume();
    }
  };
  private readonly focusListener = () => {
    this.scheduleActiveVideoResume();
  };
  private readonly youtubeMessageListener = (event: MessageEvent) => {
    this.handleYouTubeMessage(event);
  };
  private readonly contextMenuListener = (event: Event) => {
    event.preventDefault();
  };

  carouselItems = input<HomePageCarouselItemDto[] | null>(null);

  isMobileView = input(false);

  prioritizeInitialMedia = input(false);

  renderYoutubeImmediately = input(false);

  ngAfterViewInit(): void {
    // Splide only exists in the browser — never mark the view "initialized"
    // during SSR, since nothing actually mounted there.
    if (this.isBrowser()) {
      this.isViewInitialized.set(true);
    }
    this.rebuildSplide();
    this.registerBrowserLifecycleListeners();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.isViewInitialized.set(false);
    this.unregisterBrowserLifecycleListeners();
    this.clearYouTubeRevealFallbacks();

    if (this.rebuildTimeoutId) {
      clearTimeout(this.rebuildTimeoutId);
      this.rebuildTimeoutId = undefined;
    }

    if (this.videoControlTimeoutId) {
      clearTimeout(this.videoControlTimeoutId);
      this.videoControlTimeoutId = undefined;
    }

    if (this.trackpadCooldownTimeoutId) {
      clearTimeout(this.trackpadCooldownTimeoutId);
      this.trackpadCooldownTimeoutId = undefined;
    }

    this.videoCommandTimeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.videoCommandTimeoutIds.clear();
    this.cleanupVideoInteraction();
    this.cleanupTrackpadNavigation();
    this.splideInstance?.destroy(true);
    this.splideInstance = undefined;
    this.lastSlidesSignature = '';
  }

  slideShowItemList = signal<Array<SlideItemView>>([]);
  private readonly youtubeIframeLoadState = signal<Record<number, boolean>>({});
  readonly hasEnoughSlidesForLoop = computed(
    () => this.slideShowItemList().length > 1
  );
  private readonly hasYouTubeSlides = computed(() =>
    this.slideShowItemList().some(
      (slide) => !!(slide.youtubeUrl || slide.youtubeUrlMobile)
    )
  );
  readonly isSingleSlide = computed(
    () => this.slideShowItemList().length === 1
  );

  private readonly platformId: object = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly resourceHints = inject(ResourceHintsService);
  public sanitizer = inject(DomSanitizer);

  constructor() {
    effect(() => {
      const cmsCarouselItems = this.carouselItems();

      if ((cmsCarouselItems?.length ?? 0) > 0) {
        this.setSlidesAndRebuildIfChanged(
          this.mapCmsCarouselItems(cmsCarouselItems)
        );
      }
    });

    effect(() => {
      const isMobileView = this.isMobileView();

      if (this.lastMobileView === undefined) {
        this.lastMobileView = isMobileView;
        return;
      }

      if (this.lastMobileView === isMobileView) {
        return;
      }

      this.lastMobileView = isMobileView;
      this.scheduleRebuildSplide();
    });
  }

  private initSplide(): void {
    const splideElement = this.getSplideElement();

    if (!splideElement) {
      return;
    }

    if (this.slideShowItemList().length === 0) {
      return;
    }

    this.splideInstance?.destroy(true);
    this.splideInstance = new Splide(splideElement, this.getSplideOptions());
    this.splideInstance.mount();

    // Prevent pausing of YouTube videos by blocking context menu and keyboard events
    this.blockVideoInteraction();

    // Setup carousel event listeners for video playback control
    this.setupCarouselVideoControls();

    // Enable horizontal trackpad/scroll wheel navigation
    this.setupTrackpadNavigation();
  }

  private getSplideOptions(): Options {
    const hasMultipleSlides = this.hasEnoughSlidesForLoop();
    const rewindVideos = hasMultipleSlides && this.hasYouTubeSlides();

    return {
      // Loop clones create extra YouTube players and repeat their startup work.
      type: hasMultipleSlides ? 'loop' : 'slide',
      rewind: rewindVideos,
      rewindByDrag: rewindVideos,
      perMove: 1,
      focus: 'center',
      updateOnMove: true,
      speed: 650,
      autoplay: false,
      pauseOnHover: true,
      easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
      fixedWidth: '52rem',
      gap: '2rem',
      breakpoints: {
        768: {
          fixedWidth: '70vw',
          gap: '0.75rem',
          perPage: 1,
          padding: { left: '15%', right: '15%' },
        },
      },
    };
  }

  private setupTrackpadNavigation(): void {
    if (!this.isBrowser() || !this.getSplideElement() || !this.splideInstance) {
      return;
    }

    this.cleanupTrackpadNavigation();

    const threshold = 30;

    this.trackpadWheelListener = (e: WheelEvent) => {
      if (!this.splideInstance || this.trackpadScrollCooldown) {
        return;
      }

      const deltaX = Math.abs(e.deltaX);
      const deltaY = Math.abs(e.deltaY);

      // Only handle horizontal scroll (trackpad swipe)
      if (deltaX < threshold || deltaY > deltaX) {
        return;
      }

      e.preventDefault();
      this.trackpadScrollCooldown = true;

      if (e.deltaX > 0) {
        this.splideInstance.go('>');
      } else {
        this.splideInstance.go('<');
      }

      if (this.trackpadCooldownTimeoutId) {
        clearTimeout(this.trackpadCooldownTimeoutId);
      }

      this.trackpadCooldownTimeoutId = setTimeout(() => {
        this.trackpadCooldownTimeoutId = undefined;
        this.trackpadScrollCooldown = false;
      }, 400);
    };

    this.getSplideElement()?.addEventListener(
      'wheel',
      this.trackpadWheelListener,
      { passive: false }
    );
  }

  private cleanupTrackpadNavigation(): void {
    if (this.trackpadCooldownTimeoutId) {
      clearTimeout(this.trackpadCooldownTimeoutId);
      this.trackpadCooldownTimeoutId = undefined;
    }

    if (this.trackpadWheelListener && this.splideRef) {
      this.splideRef.nativeElement.removeEventListener(
        'wheel',
        this.trackpadWheelListener
      );
      this.trackpadWheelListener = undefined;
    }

    this.trackpadScrollCooldown = false;
  }

  private blockVideoInteraction(): void {
    if (!this.hasBrowserSplideContext()) {
      return;
    }

    this.cleanupVideoInteraction();

    // The .yt-overlay div absorbs all pointer events, so no direct interaction
    // with the iframe is possible. This listener prevents context menu on overlays.
    const overlays =
      this.splideRef.nativeElement.querySelectorAll<HTMLElement>('.yt-overlay');
    overlays.forEach((overlay) => {
      overlay.addEventListener('contextmenu', this.contextMenuListener);
    });
  }

  private cleanupVideoInteraction(): void {
    if (!this.splideRef) {
      return;
    }

    const overlays =
      this.splideRef.nativeElement.querySelectorAll<HTMLElement>('.yt-overlay');
    overlays.forEach((overlay) => {
      overlay.removeEventListener('contextmenu', this.contextMenuListener);
    });
  }

  private setupCarouselVideoControls(): void {
    if (!this.splideInstance) {
      return;
    }

    this.syncVisibleSlideVideos();

    this.splideVideoSyncEvents.forEach((eventName) => {
      this.splideInstance.on(eventName, () => {
        this.syncVisibleSlideVideos();
      });
    });
  }

  private syncVisibleSlideVideos() {
    if (!this.isBrowser()) {
      return;
    }

    if (!this.splideInstance) {
      return;
    }

    this.getVisibleYouTubeIframes().forEach((iframe) => {
      this.ensureYouTubeIframeInitialized(iframe);
      this.sendPlayCommandToIframe(iframe);
    });
  }

  private getVisibleYouTubeIframes(): HTMLIFrameElement[] {
    return this.querySplideElements<HTMLIFrameElement>(
      '.splide__slide.is-prev .yt-frame, .splide__slide.is-next .yt-frame, .splide__slide.is-visible .yt-frame'
    ).filter((iframe) => this.isYouTubeEmbedIframe(iframe));
  }

  private isYouTubeEmbedIframe(iframe: HTMLIFrameElement): boolean {
    return (
      iframe.src.includes('youtube.com/embed') ||
      iframe.src.includes('youtube-nocookie.com/embed')
    );
  }

  private sendPlayCommandToIframe(iframe: HTMLIFrameElement): void {
    this.ensureYouTubeIframeInitialized(iframe);

    const timeoutId = setTimeout(() => {
      this.videoCommandTimeoutIds.delete(timeoutId);

      if (this.isDestroyed) {
        return;
      }

      this.postYouTubeCommand(iframe, 'mute');
      this.postYouTubeCommand(iframe, 'playVideo');
    }, 30);
    this.videoCommandTimeoutIds.add(timeoutId);
  }

  private ensureYouTubeIframeInitialized(iframe: HTMLIFrameElement): void {
    if (this.initializedYouTubeIframes.has(iframe)) {
      return;
    }

    const slideId = this.getSlideIdFromIframe(iframe);

    if (slideId === null) {
      return;
    }

    this.initializedYouTubeIframes.add(iframe);
    this.scheduleYouTubeRevealFallback(slideId);
    this.postYouTubeListeningMessage(iframe);
    this.postYouTubeCommand(iframe, 'addEventListener', ['onReady']);
    this.postYouTubeCommand(iframe, 'addEventListener', ['onStateChange']);
    this.postYouTubeCommand(iframe, 'mute');
  }

  private getIframeTargetOrigin(iframe: HTMLIFrameElement): string {
    if (!iframe.src) {
      return 'https://www.youtube.com';
    }

    return new URL(iframe.src).origin;
  }

  private scheduleActiveVideoResume(): void {
    if (!this.isBrowser()) {
      return;
    }

    if (this.videoControlTimeoutId) {
      clearTimeout(this.videoControlTimeoutId);
    }

    this.videoControlTimeoutId = setTimeout(() => {
      this.videoControlTimeoutId = undefined;

      if (!this.hasMountedSplides()) {
        this.rebuildSplide();
        return;
      }

      this.syncVisibleSlideVideos();
    }, 160);
  }

  private registerBrowserLifecycleListeners(): void {
    if (!this.isBrowser()) {
      return;
    }

    window.addEventListener('pageshow', this.pageShowListener);
    window.addEventListener('focus', this.focusListener);
    window.addEventListener('message', this.youtubeMessageListener);
    document.addEventListener(
      'visibilitychange',
      this.visibilityChangeListener
    );
  }

  private unregisterBrowserLifecycleListeners(): void {
    if (!this.isBrowser()) {
      return;
    }

    window.removeEventListener('pageshow', this.pageShowListener);
    window.removeEventListener('focus', this.focusListener);
    window.removeEventListener('message', this.youtubeMessageListener);
    document.removeEventListener(
      'visibilitychange',
      this.visibilityChangeListener
    );
  }

  private scheduleRebuildSplide() {
    if (!this.isBrowser() || !this.isViewInitialized()) {
      return;
    }

    if (this.rebuildTimeoutId) {
      clearTimeout(this.rebuildTimeoutId);
    }

    this.rebuildTimeoutId = setTimeout(() => {
      this.rebuildTimeoutId = undefined;
      this.rebuildSplide();
    }, 50);
  }

  private rebuildSplide() {
    if (!this.hasBrowserSplideContext()) {
      return;
    }

    this.initSplide();
  }

  private setSlidesAndRebuildIfChanged(slides: SlideItemView[]): void {
    const slidesSignature = this.createSlidesSignature(slides);

    if (slidesSignature === this.lastSlidesSignature) {
      return;
    }

    this.lastSlidesSignature = slidesSignature;
    this.resetYoutubeIframeLoadState(slides);
    this.slideShowItemList.set(slides);
    if (this.hasYouTubeSlides()) {
      this.resourceHints.addYouTube();
    }
    this.scheduleRebuildSplide();
  }

  private createSlidesSignature(slides: SlideItemView[]): string {
    return slides
      .map(
        (slide) =>
          `${slide.idItem}|${slide.imageUrl}|${
            slide.mobileImageUrl
          }|${this.getSafeResourceUrlKey(
            slide.youtubeUrl
          )}|${this.getSafeResourceUrlKey(slide.youtubeUrlMobile)}|${
            slide.linkUrl ?? ''
          }|${slide.itemOrder}`
      )
      .join('||');
  }

  private getSafeResourceUrlKey(url: SafeResourceUrl | null): string {
    if (!url) {
      return '';
    }

    const unsafeUrl =
      (url as unknown as { changingThisBreaksApplicationSecurity?: string })
        ?.changingThisBreaksApplicationSecurity ?? '';

    return typeof unsafeUrl === 'string' ? unsafeUrl : '';
  }

  isYouTubeIframeLoaded(slideId: number): boolean {
    if (this.renderYoutubeImmediately()) {
      return true;
    }

    return this.youtubeIframeLoadState()[slideId] ?? false;
  }

  markYouTubeIframeLoaded(slideId: number): void {
    this.clearYouTubeRevealFallback(slideId);

    this.syncYouTubeIframeLoadedDomState(slideId, true);

    this.youtubeIframeLoadState.update((currentState) => {
      if (currentState[slideId]) {
        return currentState;
      }

      return {
        ...currentState,
        [slideId]: true,
      };
    });
  }

  private resetYoutubeIframeLoadState(slides: SlideItemView[]): void {
    this.clearYouTubeRevealFallbacks();
    this.initializedYouTubeIframes = new WeakSet<HTMLIFrameElement>();

    const previousLoadState = this.youtubeIframeLoadState();
    const shouldRenderYoutubeImmediately = this.renderYoutubeImmediately();

    const nextLoadState = slides.reduce<Record<number, boolean>>(
      (accumulator, slide) => {
        if (slide.youtubeUrl || slide.youtubeUrlMobile) {
          const isLoaded = shouldRenderYoutubeImmediately
            ? true
            : previousLoadState[slide.idItem] ?? false;
          accumulator[slide.idItem] = isLoaded;
          this.syncYouTubeIframeLoadedDomState(slide.idItem, isLoaded);
        }

        return accumulator;
      },
      {}
    );

    this.youtubeIframeLoadState.set(nextLoadState);
  }

  private scheduleYouTubeRevealFallback(slideId: number): void {
    if (this.renderYoutubeImmediately()) {
      return;
    }

    this.scheduleYouTubeRevealFallbackWithDelay(
      slideId,
      this.youtubeRevealFallbackDelay
    );
  }

  private scheduleYouTubeRevealFallbackWithDelay(
    slideId: number,
    delay: number
  ): void {
    this.clearYouTubeRevealFallback(slideId);

    const timeoutId = setTimeout(() => {
      this.youtubeRevealTimeoutIds.delete(slideId);
      this.markYouTubeIframeLoaded(slideId);
    }, delay);

    this.youtubeRevealTimeoutIds.set(slideId, timeoutId);
  }

  private getSlideIdFromIframe(iframe: HTMLIFrameElement): number | null {
    const iframeSlideId = Number(iframe.dataset['slideId']);

    if (Number.isFinite(iframeSlideId)) {
      return iframeSlideId;
    }

    const parentSlide = iframe.closest('.splide__slide');
    const slideId = Number(parentSlide?.getAttribute('data-slide-id'));
    return Number.isFinite(slideId) ? slideId : null;
  }

  private syncYouTubeIframeLoadedDomState(
    slideId: number,
    loaded: boolean
  ): void {
    if (!this.isBrowser()) {
      return;
    }

    const selector = `.yt-frame[data-slide-id="${slideId}"]`;
    const iframes = this.querySplideElements<HTMLIFrameElement>(selector);

    iframes.forEach((iframe) => {
      iframe.classList.toggle('yt-frame--loaded', loaded);

      const wrapper = iframe.closest('.yt-wrapper');

      if (!wrapper) {
        return;
      }

      wrapper.classList.toggle('yt-wrapper--loaded', loaded);

      const placeholder = wrapper.querySelector(
        '.yt-loading-placeholder'
      ) as HTMLElement | null;

      if (!placeholder) {
        return;
      }

      placeholder.style.display = loaded ? 'none' : '';
      placeholder.setAttribute('aria-hidden', loaded ? 'true' : 'false');
    });
  }

  private clearYouTubeRevealFallback(slideId: number): void {
    const timeoutId = this.youtubeRevealTimeoutIds.get(slideId);

    if (!timeoutId) {
      return;
    }

    clearTimeout(timeoutId);
    this.youtubeRevealTimeoutIds.delete(slideId);
  }

  private clearYouTubeRevealFallbacks(): void {
    this.youtubeRevealTimeoutIds.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });

    this.youtubeRevealTimeoutIds.clear();
  }

  private handleYouTubeMessage(event: MessageEvent): void {
    if (!this.isBrowser()) {
      return;
    }

    if (!this.isYouTubeOrigin(event.origin)) {
      return;
    }

    const data = this.parseYouTubeMessageData(event.data);

    if (!data) {
      return;
    }

    const slideId = this.resolveSlideIdFromMessage(event.source, data);

    if (slideId === null) {
      return;
    }

    if (data['event'] === 'onReady') {
      const iframe = this.getIframeFromMessageSource(event.source);

      if (iframe) {
        this.sendPlayCommandToIframe(iframe);
      }

      return;
    }

    if (!Number.isFinite(slideId) || !this.shouldRevealYouTubeIframe(data)) {
      return;
    }

    this.markYouTubeIframeLoaded(slideId);
  }

  private isYouTubeOrigin(origin: string): boolean {
    return (
      origin === 'https://www.youtube.com' ||
      origin === 'https://www.youtube-nocookie.com'
    );
  }

  private parseYouTubeMessageData(
    data: unknown
  ): Record<string, unknown> | null {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data) as Record<string, unknown>;
      } catch {
        return null;
      }
    }

    if (typeof data === 'object' && data !== null) {
      return data as Record<string, unknown>;
    }

    return null;
  }

  private getIframeFromMessageSource(
    source: MessageEventSource | null
  ): HTMLIFrameElement | null {
    if (!source) {
      return null;
    }

    return this.findIframeByWindowSource(source);
  }

  private resolveSlideIdFromMessage(
    source: MessageEventSource | null,
    data: Record<string, unknown>
  ): number | null {
    const rawSlideId = data['id'];

    if (typeof rawSlideId === 'number' && Number.isFinite(rawSlideId)) {
      return rawSlideId;
    }

    if (typeof rawSlideId === 'string') {
      const parsedSlideId = Number(rawSlideId);

      if (Number.isFinite(parsedSlideId)) {
        return parsedSlideId;
      }
    }

    if (!source) {
      return null;
    }

    const matchedIframe = this.findIframeByWindowSource(source);

    if (!matchedIframe) {
      return null;
    }

    const matchedSlideId = Number(matchedIframe.dataset['slideId']);
    return Number.isFinite(matchedSlideId) ? matchedSlideId : null;
  }

  private shouldRevealYouTubeIframe(data: Record<string, unknown>): boolean {
    const eventName = data['event'];
    const info =
      typeof data['info'] === 'object' && data['info'] !== null
        ? (data['info'] as Record<string, unknown>)
        : null;

    if (eventName === 'onReady') {
      return false;
    }

    const playerState = info?.['playerState'];

    if (
      typeof playerState === 'number' &&
      this.youtubeReadyStateValues.has(playerState)
    ) {
      return true;
    }

    const currentTime = info?.['currentTime'];
    return typeof currentTime === 'number' && currentTime > 0;
  }

  private postYouTubeListeningMessage(iframe: HTMLIFrameElement): void {
    const targetOrigin = this.getIframeTargetOrigin(iframe);
    const slideId = Number(iframe.dataset['slideId']);
    const messageId = Number.isFinite(slideId)
      ? slideId
      : iframe.dataset['slideId'];

    iframe.contentWindow?.postMessage(
      JSON.stringify({
        event: 'listening',
        id: messageId,
        channel: 'widget',
      }),
      targetOrigin
    );
  }

  private postYouTubeCommand(
    iframe: HTMLIFrameElement,
    func: string,
    args: unknown[] = []
  ): void {
    const targetOrigin = this.getIframeTargetOrigin(iframe);

    iframe.contentWindow?.postMessage(
      JSON.stringify({
        event: 'command',
        func,
        args,
      }),
      targetOrigin
    );
  }

  private mapCmsCarouselItems(
    items: HomePageCarouselItemDto[]
  ): SlideItemView[] {
    const mappedItems = items
      .filter(
        (item) =>
          !!item?.youtubeUrl ||
          !!item?.youtubeUrlMobile ||
          !!item?.imageUrl ||
          !!item?.mobileImageUrl
      )
      .map((item, index) => {
        return {
          idItem: item.id ?? index + 1,
          imageUrl: item.imageUrl ?? item.mobileImageUrl ?? null,
          mobileImageUrl: item.mobileImageUrl ?? item.imageUrl ?? null,
          youtubeUrl: item.youtubeUrl
            ? this.sanitizer.bypassSecurityTrustResourceUrl(
                this.convertToYouTubeEmbedUrl(item.youtubeUrl)
              )
            : null,
          youtubeUrlMobile: item.youtubeUrlMobile
            ? this.sanitizer.bypassSecurityTrustResourceUrl(
                this.convertToYouTubeEmbedUrl(item.youtubeUrlMobile)
              )
            : null,
          linkUrl: item.linkUrl?.trim() || null,
          itemOrder: item.orderIndex ?? index,
        };
      });

    return mappedItems.sort((a, b) => a.itemOrder - b.itemOrder);
  }

  getSlideYoutubeUrl(slide: SlideItemView): SafeResourceUrl | null {
    if (this.isMobileView()) {
      return slide.youtubeUrlMobile ?? slide.youtubeUrl ?? null;
    }

    return slide.youtubeUrl ?? slide.youtubeUrlMobile ?? null;
  }

  isShowingVerticalVideo(slide: SlideItemView): boolean {
    if (this.isMobileView()) {
      return !!slide.youtubeUrlMobile;
    }
    return !slide.youtubeUrl && !!slide.youtubeUrlMobile;
  }

  private convertToYouTubeEmbedUrl(youtubeUrl: string): string {
    const url = new URL(youtubeUrl);

    let videoId = '';

    if (url.hostname.includes('youtu.be')) {
      videoId = url.pathname.slice(1);
    } else if (url.searchParams.get('v')) {
      videoId = url.searchParams.get('v')!;
    } else if (url.pathname.includes('/shorts/')) {
      videoId = url.pathname.split('/shorts/')[1]?.split('/')[0] ?? '';
    } else if (url.pathname.includes('/embed/')) {
      videoId = url.pathname.split('/embed/')[1]?.split('/')[0] ?? '';
    }

    const embedUrl = new URL(
      `https://www.youtube-nocookie.com/embed/${videoId}`
    );

    embedUrl.searchParams.set('autoplay', '1');
    embedUrl.searchParams.set('mute', '1');
    embedUrl.searchParams.set('playsinline', '1');
    embedUrl.searchParams.set('controls', '0');
    embedUrl.searchParams.set('disablekb', '1');
    embedUrl.searchParams.set('showinfo', '0');
    embedUrl.searchParams.set('autohide', '1');
    embedUrl.searchParams.set('loop', '1');
    embedUrl.searchParams.set('fs', '0');
    embedUrl.searchParams.set('modestbranding', '1');
    embedUrl.searchParams.set('rel', '0');
    embedUrl.searchParams.set('cc_load_policy', '0');
    embedUrl.searchParams.set('iv_load_policy', '3');
    embedUrl.searchParams.set('enablejsapi', '1');
    embedUrl.searchParams.set('playlist', videoId);
    if (isPlatformBrowser(this.platformId)) {
      embedUrl.searchParams.set('origin', window.location.origin);
    }
    return embedUrl.toString();
  }

  isLinkExternal(link: string | null): boolean {
    if (!link) {
      return false;
    }

    const resolvedUrl = this.resolveLinkUrl(link);

    if (!resolvedUrl) {
      return false;
    }

    return !this.isMemorabidDomain(resolvedUrl.hostname);
  }

  getInternalRouterLink(link: string | null): UrlTree | string {
    if (!link) {
      return '/';
    }

    const resolvedUrl = this.resolveLinkUrl(link);

    if (!resolvedUrl) {
      return link;
    }

    const pathname = resolvedUrl.pathname.replace(/^\/+/, '');
    const segments = pathname.split('/').filter(Boolean);
    const lang = this.languageService.userSelectedLanguage?.code ?? 'it';

    if (segments[0] !== 'it' && segments[0] !== 'en') {
      segments.unshift(lang);
    }

    const routePath = `/${segments.join('/')}${resolvedUrl.search}${
      resolvedUrl.hash
    }`;
    return this.router.parseUrl(routePath || '/');
  }

  private resolveLinkUrl(link: string): URL | null {
    const baseOrigin = environment.BaseUrl;

    try {
      return new URL(link, baseOrigin);
    } catch {
      return null;
    }
  }

  private isMemorabidDomain(hostname: string): boolean {
    const host = hostname.toLowerCase();

    return (
      host === 'memorabid.com' ||
      host.endsWith('.memorabid.com') ||
      host === 'localhost' ||
      host === '127.0.0.1'
    );
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private getSplideElement(): HTMLElement | null {
    return this.splideRef?.nativeElement ?? null;
  }

  private hasBrowserSplideContext(): boolean {
    return this.isBrowser() && !!this.getSplideElement();
  }

  private querySplideElements<T extends Element>(selector: string): T[] {
    const splideElement = this.getSplideElement();
    return splideElement
      ? (Array.from(splideElement.querySelectorAll(selector)) as T[])
      : [];
  }

  private getManagedIframes(): HTMLIFrameElement[] {
    return this.querySplideElements<HTMLIFrameElement>('iframe[data-slide-id]');
  }

  private findIframeByWindowSource(
    source: MessageEventSource
  ): HTMLIFrameElement | null {
    return (
      this.getManagedIframes().find(
        (iframe) => iframe.contentWindow === source
      ) ?? null
    );
  }

  private hasMountedSplides(): boolean {
    return !!this.splideInstance;
  }
}
